export const TOOL_DOMAIN_SCORE = 'domain_score';
export const TOOL_DOMAIN_ANALYSIS = 'domain_analysis';
export const TOOL_LOOKUP = 'lookup';
export const TOOL_BLOCKLIST_LOOKUP = 'blocklist_lookup';
export const TOOL_HEADER_ANALYSIS = 'header_analysis';
export const TOOL_SPF_POLICY_TEST = 'spf_policy_test';
export const TOOL_CIDR_CALCULATOR = 'cidr_calculator';
export const TOOL_DKIM_GENERATOR = 'dkim_generator';

export async function initPosthog({ loadScript = defaultLoadScript } = {}) {
  if (!shouldLoadPosthog()) {
    return null;
  }

  const posthog = await loadScript(sdkUrl());
  const client = posthog ?? window.posthog;
  if (!client?.init) {
    throw new Error('PostHog SDK did not load');
  }
  client.init(import.meta.env.VITE_POSTHOG_API_TOKEN, posthogInitConfig());
  return client;
}

export function posthogInitConfig() {
  return {
    api_host: import.meta.env.VITE_POSTHOG_API_URL,
    ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
    defaults: '2026-08-29',
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    disable_session_recording: true,
    disable_surveys: true,
    advanced_disable_flags: true,
    autocapture: true
  };
}

export function shouldLoadPosthog(search = window.location.search) {
  return (
    Boolean(import.meta.env.VITE_POSTHOG_API_TOKEN) &&
    Boolean(import.meta.env.VITE_POSTHOG_API_URL) &&
    !isPdfLayout(search)
  );
}

export function isPdfLayout(search = window.location.search) {
  return new URLSearchParams(search).get('layout') === 'pdf';
}

export function sdkUrl() {
  const apiHost = import.meta.env.VITE_POSTHOG_API_URL;
  return apiHost ? `${apiHost}/static/array.js` : '';
}

function defaultLoadScript(src) {
  return new Promise((resolve, reject) => {
    if (window.posthog?.init) {
      resolve(window.posthog);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.posthog);
    script.onerror = () =>
      reject(new Error(`Failed to load PostHog from ${src}`));
    document.head.appendChild(script);
  });
}

export function getPosthog() {
  return window.posthog ?? null;
}

export function captureEvent(event, properties = {}) {
  getPosthog()?.capture(event, withBaseProperties(properties));
}

export function withBaseProperties(properties = {}) {
  return {
    path: window.location.pathname,
    ...properties
  };
}

export function captureToolUsed({ tool, properties = {} }) {
  captureEvent('tool_used', withoutEmptyValues({ tool, ...properties }));
}

function withoutEmptyValues(properties) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value != null)
  );
}

export function captureDetailsExpanded({ tool, testedDomain }) {
  captureEvent('details_expanded', {
    tool,
    tested_domain: testedDomain
  });
}

export function captureReportRequested({
  step,
  tool,
  testedDomain,
  emailDomain
} = {}) {
  captureEvent('report_requested', {
    step,
    tool,
    tested_domain: testedDomain,
    ...(emailDomain ? { email_domain: emailDomain } : {})
  });
}

export function captureReportDownloaded({ tool, testedDomain }) {
  captureEvent('report_downloaded', {
    tool,
    tested_domain: testedDomain
  });
}

export function identifyFromEmailReport(email) {
  const normalized = String(email ?? '')
    .trim()
    .toLowerCase();
  if (!normalized.includes('@')) {
    return;
  }

  getPosthog()?.identify(normalized, {
    email: normalized,
    first_identified_from: 'tools:email_report',
    email_domain: normalized.split('@')[1] || undefined
  });
}

export function captureContactClicked() {
  captureEvent('contact_clicked');
}

export function captureSignupClicked() {
  captureEvent('signup_clicked');
}

export function dmarcProviderHostsFromRua(rua) {
  const values = Array.isArray(rua) ? rua : String(rua ?? '').split(',');
  const hosts = [];

  for (const value of values) {
    const mailbox = String(value)
      .trim()
      .replace(/^mailto:/i, '')
      .split('!')[0];
    const host = mailbox.includes('@')
      ? mailbox.split('@').pop().toLowerCase()
      : undefined;
    if (host && !hosts.includes(host)) {
      hosts.push(host);
    }
  }

  return hosts;
}
